import { type Brand } from '../entity/api/brand';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { getQueryClient } from '../sdk/request';
import { groupPostHogWorkspace } from '../services/posthog';
import { getStores } from '../services/storage';
import {
  fetchBrandsListImperative,
  getBrandsListFromCache,
} from '../services/storage/brands-queries';
import {
  getOrganizationBillingFromCache,
  syncBillingImperative,
} from '../services/storage/organization-billing-queries';
import { fetchOrganizationUsersListImperative } from '../services/storage/organization-users-queries';
import { getOrganizationFromCache } from '../services/storage/organizations-queries';
import { UIKey } from '../services/storage/ui-key-value';
import { getAuthUserFromCache, updateUserImperative } from '../services/storage/users-queries';
import { setSelectedBrand } from './brand';

export type OrganizationSwitchResult = {
  success: boolean;
  selectedBrand?: Brand;
  error?: string;
};

export async function switchToOrganization(
  organizationId: string
): Promise<OrganizationSwitchResult> {
  try {
    const stores = getStores();
    const queryClient = getQueryClient();
    const isCurrentlyLoading =
      stores.uiKeyValue.getState().getFromStore(UIKey.IS_ORG_LOADING) ?? false;

    if (isCurrentlyLoading === true) {
      return {
        success: false,
        error: 'Organization is already loading',
      };
    }

    stores.uiKeyValue.getState().setToStore(UIKey.IS_ORG_LOADING as const, true);

    // Get current brands to find ones for the target organization (from TanStack Query cache)
    let allBrands = getBrandsListFromCache(queryClient) ?? [];

    // If cache is empty, fetch brands first
    if (allBrands.length === 0) {
      allBrands = await fetchBrandsListImperative(queryClient);
    }

    // Filter brands for the target organization
    const orgBrands = allBrands.filter(function (brand) {
      return brand != null && brand.organizationId === organizationId;
    });

    if (orgBrands.length === 0) {
      // Reset loading flag before returning
      stores.uiKeyValue.getState().setToStore(UIKey.IS_ORG_LOADING as const, false);
      return {
        success: false,
        error: 'No brands found for the selected organization',
      };
    }

    // Select the first brand for the organization
    const selectedBrand = orgBrands[0];
    if (selectedBrand == null) {
      // Reset loading flag before returning
      stores.uiKeyValue.getState().setToStore(UIKey.IS_ORG_LOADING as const, false);
      return {
        success: false,
        error: 'No valid brand found for the selected organization',
      };
    }

    // Check what data is already available to avoid unnecessary API calls
    const dataLoadingPromises = [];

    // Only load billing data if it's not already available
    const billingData = getOrganizationBillingFromCache(queryClient, organizationId);
    if (billingData == null) {
      // Sync billing with payment provider to ensure we have the latest data
      dataLoadingPromises.push(syncBillingImperative(queryClient, organizationId));
    }

    // Refresh organization users
    dataLoadingPromises.push(fetchOrganizationUsersListImperative(queryClient, organizationId));

    // Note: Brands are already fetched above if cache was empty

    // Note: Articles are not prefetched here because the blogs component (useArticlesByBrandId hook)
    // always fetches articles on mount. Prefetching here would cause duplicate API calls with different
    // query parameters (no params vs pagination params), preventing TanStack Query cache deduplication.

    // Wait for any necessary data loading
    const results = await Promise.allSettled(dataLoadingPromises);

    // Now update user's selected organization and brand (skip PATCH if already the same — avoids duplicate)
    const currentUser = getAuthUserFromCache(queryClient);
    const alreadySame =
      currentUser?.selectedOrganizationId === organizationId &&
      currentUser?.selectedBrandId === selectedBrand._id;
    if (!alreadySame) {
      await updateUserImperative(queryClient, {
        selectedOrganizationId: organizationId,
        selectedBrandId: selectedBrand._id,
      });
    }

    // Log any failed requests for debugging
    for (let index = 0; index < results.length; index += 1) {
      const result = results[index];
      if (result.status === 'rejected') {
        captureException(
          new Error(`Data loading failed for request ${index}: ${result.reason}`),
          {
            file: 'shared/organization-switcher.ts',
            method: 'switchToOrganization',
            infoMessage: 'Failed to load organization data',
            organizationId,
            requestIndex: index,
          },
          trackError
        );
      }
    }

    const orgForGroup = getOrganizationFromCache(queryClient, organizationId);
    const billingForGroup = getOrganizationBillingFromCache(queryClient, organizationId);
    if (orgForGroup != null && billingForGroup != null) {
      groupPostHogWorkspace(orgForGroup, billingForGroup);
    }

    // Set the selected brand (this handles additional brand-specific logic)
    await setSelectedBrand(selectedBrand._id);
    stores.uiKeyValue.getState().setToStore(UIKey.IS_ORG_LOADING as const, false);

    return {
      success: true,
      selectedBrand,
    };
  } catch (err) {
    // Log the error for debugging
    captureException(
      err instanceof Error ? err : new Error('Unknown error during organization switch'),
      {
        file: 'shared/organization-switcher.ts',
        method: 'switchToOrganization',
        infoMessage: 'Failed to switch organization',
        organizationId,
      },
      trackError
    );

    // Make sure to reset loading state on error
    getStores()
      .uiKeyValue.getState()
      .setToStore(UIKey.IS_ORG_LOADING as const, false);

    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error occurred',
    };
  }
}

export function getBrandsForOrganization(organizationId: string): Brand[] {
  const queryClient = getQueryClient();
  const allBrands = getBrandsListFromCache(queryClient) ?? [];

  return allBrands.filter(function (brand): brand is Brand {
    return brand != null && brand.organizationId === organizationId;
  });
}

export function organizationHasBrands(organizationId: string): boolean {
  return getBrandsForOrganization(organizationId).length > 0;
}
